import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaMoneyBillWave, FaLandmark, FaFileInvoiceDollar,
  FaClock, FaChartLine,
  FaCheck,
} from 'react-icons/fa';

const services = [
  {
    icon: FaMoneyBillWave,
    iconBg: 'bg-blue-500',
    name: 'Automated Pay Runs',
    tag: 'Payroll Calculations',
    tagColor: 'bg-blue-50 text-payday-blue border-blue-100',
    desc: 'Run payroll in minutes, not hours. We handle the math, the deposits, and the deadlines — on whatever schedule your business runs on.',
    features: ['Direct Deposit & Bank Sync', 'CPP & EI Deductions Calculated Automatically', 'Statutory Holiday Pay Built In', 'Custom Earnings & Deductions'],
    popular: true,
  },
  {
    icon: FaLandmark,
    iconBg: 'bg-purple-500',
    name: 'CRA Source Remittances',
    tag: 'CRA Compliance',
    tagColor: 'bg-purple-50 text-purple-600 border-purple-100',
    desc: 'Missed remittance deadlines mean penalties. We calculate and file your CPP, EI, and income tax deductions with the CRA every time, on time.',
    features: ['Source Deductions Computed Automatically', 'Remittances Filed Monthly', 'Ongoing Compliance Checks', 'PD7A Reporting Handled For You'],
    popular: false,
  },
  {
    icon: FaFileInvoiceDollar,
    iconBg: 'bg-green-500',
    name: 'T4 & T4A Year-End Filing',
    tag: 'Year-End Compliance',
    tagColor: 'bg-green-50 text-green-600 border-green-100',
    desc: 'Tax season, handled. We prepare, file, and deliver every T4 and T4A slip to your employees — no spreadsheets, no last-minute scrambling.',
    features: ['T4s Generated Automatically', 'CRA XML Filing Included', 'Slips Delivered Digitally to Staff', 'Year-End Adjustments Reviewed'],
    popular: false,
  },
  {
    icon: FaClock,
    iconBg: 'bg-amber-500',
    name: 'Record of Employment (ROE)',
    tag: 'Service Canada Sync',
    tagColor: 'bg-amber-50 text-amber-600 border-amber-100',
    desc: 'When an employee leaves, timing matters. We calculate final pay and submit the ROE to Service Canada right away, so nothing falls behind.',
    features: ['Final Pay Calculated Accurately', 'ROEs Filed Directly With Service Canada', 'Layoff & Termination Payouts Handled', 'Secure, Searchable Record Archive'],
    popular: false,
  },
  {
    icon: FaChartLine,
    iconBg: 'bg-teal-500',
    name: 'Employee Self-Service Portal',
    tag: 'Self-Serve Portal',
    tagColor: 'bg-teal-50 text-teal-600 border-teal-100',
    desc: 'Fewer emails asking for pay stubs. Employees log in anytime to view payslips, grab their T4s, and update their own banking details.',
    features: ['Web & Mobile Access', 'Full Payslip History', 'Instant T4 Downloads', 'Employees Update Their Own Bank Info'],
    popular: false,
  },
];

export default function Products() {
  React.useEffect(() => {
    document.title = "OrientalPay | Payroll & CRA Compliance";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute('content', 'Payroll services built for Canadian businesses: automated pay runs, CRA source remittances, T4/T4A year-end filing, ROE submissions, and an employee self-service portal.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden bg-gray-900">
        {/* Background Video */}
        <video
          className="absolute inset-0 w-full h-full object-cover object-center opacity-80"
          autoPlay
          loop
          muted
          playsInline
          poster="/Payroll.avif"
          preload="metadata"
        >
          <source src="/payroll.mp4" type="video/mp4" />
        </video>
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1e45] via-[#0d1e45]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d1e45]/80 via-transparent to-transparent" />

        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 pb-20 pt-40 w-full">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-300 text-xs font-bold uppercase tracking-widest mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            Our Services
          </span>
          <h1 className="text-5xl md:text-7xl font-black text-white leading-[1.05] mb-6 max-w-3xl">
            Payroll That Keeps<br />
            <span className="text-transparent bg-clip-text"
              style={{ backgroundImage: 'linear-gradient(90deg, #ffbe1a, #ffd966)' }}>
              You CRA-Compliant
            </span>
          </h1>
          <p className="text-blue-100 text-base md:text-lg font-medium leading-relaxed max-w-xl">
            From pay runs to year-end filings, we manage the calculations, remittances, and deadlines — so your business stays compliant without the paperwork.
          </p>
        </div>
      </section>



      {/* Services Grid */}
      <section className="py-16 px-6 section-pastel-blue">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map(({ icon: Icon, iconBg, name, tag, tagColor, desc, features, popular }) => (
              <div
                key={name}
                className={`relative card-wagepoint p-6 flex flex-col gap-4 ${
                  popular ? 'ring-2 ring-payday-blue shadow-lg' : ''
                }`}
              >
                {popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-5 py-1 btn-payday-blue text-xs font-bold shadow whitespace-nowrap">
                    Recommended
                  </div>
                )}

                <div className="flex items-start justify-between">
                  <div className={`w-11 h-11 rounded-2xl ${iconBg} flex items-center justify-center shadow-sm`}>
                    <Icon className="text-white text-lg" />
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${tagColor}`}>{tag}</span>
                </div>

                <div>
                  <h2 className="text-lg font-extrabold text-gray-900 mb-2">{name}</h2>
                  <p className="text-gray-500 text-sm leading-relaxed mb-1">{desc}</p>
                </div>

                <ul className="flex flex-col gap-2 flex-1">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                      <FaCheck className="text-payday-blue text-xs flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/contact">
                  <button className={`w-full mt-2 py-2.5 rounded-full text-sm font-bold transition-all duration-250 ${
                    popular
                      ? 'btn-payday-blue'
                      : 'border border-gray-200 text-gray-600 hover:border-payday-blue hover:text-payday-blue bg-white'
                  }`}>
                    Book Service →
                  </button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}